import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import './UpdateProfile.scss';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import { DateTime } from 'luxon';
import {
  getUserProfile,
  getUserProfilePic,
  UpdateUserProfile,
} from '../../../redux/network/userApi';
import { setAlertMessage, setAlertType } from '../../../redux/reducers/alertSlice';
import { setIsLoginDialog } from '../../../redux/reducers/loginSlice';
import { GET_LEADGEN_PROFILE, POST_OTP_REGISTRATION, POST_REGISTER, POST_SIGNUP_COMPLETE } from '../../../config/endpoints';

const emailRegex = /^[\w-]+(\.[\w-]+)*@([\w-]+\.)+[a-zA-Z]{2,7}$/;

const createOtpState = (overrides = {}) => ({ requesting: false, verifying: false, requested: false, verified: false, error: '', otp: '', registrationId: '', ...overrides });
const OTP_LENGTH = 4;

const buildFullName = (f, m, l) => {
  let s = '';
  if (f) s += f;
  if (m) s += (s ? ' ' : '') + m;
  if (l) s += (s ? ' ' : '') + l;
  return s;
};

const splitFullName = (full) => {
  const arr = (full || '').trim().split(/\s+/).filter(Boolean);
  let firstName = '', middleName = '', lastName = '';
  if (arr.length === 1) firstName = arr[0];
  else if (arr.length === 2) { firstName = arr[0]; lastName = arr[1]; }
  else if (arr.length === 3) { firstName = arr[0]; middleName = arr[1]; lastName = arr[2]; }
  else if (arr.length > 3) { firstName = arr[0]; middleName = arr[1]; lastName = arr.slice(2).join(' '); }
  return { firstName, middleName, lastName };
};

const validDOB = (dob) => {
  if (!/^\d{2}-\d{2}-\d{4}$/.test(dob || '')) return false;
  const dt = DateTime.fromFormat(dob, 'dd-MM-yyyy');
  if (!dt.isValid) return false;
  return dt <= DateTime.now().startOf('day');
};

const normalizeDob = (dob) => {
  if (!dob && dob !== 0) return '';
  if (typeof dob === 'number') {
    const dt = DateTime.fromMillis(dob);
    if (dt.isValid) return dt.toFormat('dd-MM-yyyy');
    return '';
  }
  const str = String(dob).trim();
  if (!str) return '';
  if (/^\d{2}-\d{2}-\d{4}$/.test(str)) return str;
  const iso = DateTime.fromISO(str);
  if (iso.isValid) return iso.toFormat('dd-MM-yyyy');
  const ymd = DateTime.fromFormat(str, 'yyyy-MM-dd');
  if (ymd.isValid) return ymd.toFormat('dd-MM-yyyy');
  return '';
};

const normalizeGender = (gender) => {
  if (!gender && gender !== 0) return '';
  const g = String(gender).trim().toLowerCase();
  if (!g) return '';
  if (g === 'male') return 'Male';
  if (g === 'female') return 'Female';
  if (g === 'other') return 'Other';
  return gender;
};

const toText = (val) => {
  if (val === null || val === undefined) return '';
  if (typeof val === 'number') return String(val);
  return val;
};

const normalizeAreas = (areas) => {
  if (Array.isArray(areas)) return areas;
  if (typeof areas === 'string') return areas.split(',').map((s) => s.trim()).filter(Boolean);
  return [];
};

const toNumberIfNumeric = (val) => {
  if (val === null || val === undefined) return '';
  if (typeof val === 'number') return val;
  const trimmed = String(val).trim();
  if (!trimmed) return '';
  const num = Number(trimmed);
  return Number.isFinite(num) ? num : val;
};

const asString = (val) => {
  if (!val) return '';
  if (typeof val === 'string') return val;
  if (typeof val === 'object') {
    if (typeof val.token === 'string') return val.token;
    if (typeof val.jwt === 'string') return val.jwt;
    if (typeof val.accessToken === 'string') return val.accessToken;
  }
  return '';
};

const deriveTokenFromLead = (lead) => {
  if (!lead) return '';
  const profile = lead.profile || {};
  const auth = lead.auth || {};
  const contact = lead.contact || {};
  const possibilities = [
    lead.token,
    lead.jwt,
    lead.jwtToken,
    lead.authToken,
    lead.sessionToken,
    lead.accessToken,
    auth.token,
    auth.jwt,
    auth.accessToken,
    profile.token,
    profile.jwt,
    profile.accessToken,
    contact.token,
    contact.jwt,
    lead?.token?.token,
    lead?.token?.jwt,
    lead?.token?.accessToken,
    auth?.token?.token,
    auth?.token?.jwt,
    auth?.token?.accessToken,
    profile?.token?.token,
    profile?.token?.jwt,
    profile?.token?.accessToken,
    contact?.token?.token,
    contact?.token?.jwt,
    contact?.token?.accessToken,
    lead?.authDetails?.token,
    lead?.authDetails?.jwt,
    lead?.authDetails?.accessToken,
    lead?.credentials?.token,
    lead?.credentials?.jwt,
    lead?.credentials?.accessToken,
  ].map(asString);
  return possibilities.find(Boolean) || '';
};

const deriveUserIdFromLead = (lead) => {
  if (!lead) return '';
  const profile = lead.profile || {};
  const identity = lead.identity || {};
  const contact = lead.contact || {};
  const possibilities = [
    lead.userId,
    lead._id,
    lead.id,
    profile.userId,
    profile._id,
    profile.id,
    identity.userId,
    identity._id,
    identity.id,
    contact.userId,
    contact.id,
    contact._id,
    lead?.token?.userId,
    profile?.token?.userId,
    identity?.token?.userId,
    contact?.token?.userId,
    lead?.credentials?.userId,
    profile?.credentials?.userId,
  ];
  return possibilities.find((val) => typeof val === 'string' && val) || '';
};

const extractLeadFromResponse = (payload) => {
  if (!payload) return null;
  if (payload.user) return payload.user;
  if (payload.data) {
    if (Array.isArray(payload.data)) return payload.data[0] || null;
    if (payload.data.user) return payload.data.user;
    if (payload.data.lead) return payload.data.lead;
    if (payload.data.profile) return payload.data.profile;
    if (payload.data.data) {
      const inner = payload.data.data;
      if (Array.isArray(inner)) return inner[0] || null;
      if (inner.user) return inner.user;
      if (inner.lead) return inner.lead;
      if (inner.profile) return inner.profile;
      return inner;
    }
    return payload.data;
  }
  if (Array.isArray(payload)) return payload[0] || null;
  return payload;
};


export default function UpdateProfile() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [jwt, setJwt] = useState(() => {
    if (typeof window === 'undefined') return '';
    try { return localStorage.getItem('BQS_TOKEN') || ''; } catch { return ''; }
  });
  const [userId, setUserId] = useState(() => {
    if (typeof window === 'undefined') return '';
    try { return localStorage.getItem('BQS__ID') || ''; } catch { return ''; }
  });
  const biginId = useMemo(() => {
    try {
      const search = location?.search || '';
      const params = new URLSearchParams(search);
      return params.get('biginId') || '';
    } catch {
      return '';
    }
  }, [location?.search]);

  const [leadState, setLeadState] = useState({ data: null, loading: !!biginId, error: null, loaded: false });

  // Gate: when arriving via biginId link, phone must be verified before showing the form
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [gateOtpState, setGateOtpState] = useState(() => createOtpState());
  const [gateAttempts, setGateAttempts] = useState(0);
  const [isBlocked, setIsBlocked] = useState(false);
  const gateOtpInputRefs = useRef([]);

  const autoAuthRef = useRef(false);
  const linkGuardRef = useRef(false);
  const resetAuthRef = useRef(false);
  const otpInputRefs = useRef([]);
  const [showDemo, setShowDemo] = useState(false);

  useEffect(() => {
    let allow = false;
    try { allow = sessionStorage.getItem('ALLOW_PROFILE_UPDATE') === '1'; } catch { }
    if (biginId || allow) {
      if (biginId) {
        try { sessionStorage.setItem('ALLOW_PROFILE_UPDATE', '1'); } catch { }
      }
      return;
    }
    try { sessionStorage.removeItem('ALLOW_PROFILE_UPDATE'); } catch { }
    if (linkGuardRef.current) return;
    linkGuardRef.current = true;
    dispatch(setAlertMessage('Please use the email link to access profile update.'));
    dispatch(setAlertType('error'));
    navigate('/');
  }, [biginId, dispatch, navigate]);

  useEffect(() => {
    if (!biginId || resetAuthRef.current) return;
    resetAuthRef.current = true;
    try {
      localStorage.removeItem('BQS_TOKEN');
      localStorage.removeItem('BQS__ID');
      localStorage.removeItem('PROFILE_COMPLETED');
      localStorage.removeItem('LOGIN_COUNT');
    } catch { }
    setJwt('');
    setUserId('');
  }, [biginId]);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    if (!jwt) return;
    if (biginId) return;
    dispatch(getUserProfile(jwt));
    dispatch(getUserProfilePic(jwt));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jwt, biginId]);

  useEffect(() => {
    if (!biginId) {
      setLeadState((prev) => (prev.loading || prev.data ? { loading: false, data: null, loaded: false } : prev));
      return;
    }
    let ignore = false;
    setLeadState({ loading: true, data: null, loaded: false });
    fetch(`${GET_LEADGEN_PROFILE}?biginId=${encodeURIComponent(biginId)}`)
      .then((res) => {
        if (!res.ok) throw new Error('failed');
        return res.json();
      })
      .then((json) => {
        if (ignore) return;
        const extracted = extractLeadFromResponse(json);
        const nextData = extracted && typeof extracted === 'object' && !Array.isArray(extracted) && Object.keys(extracted).length === 0 ? null : extracted;
        setLeadState({ loading: false, data: nextData, loaded: true });
      })
      .catch(() => {
        if (ignore) return;
        setLeadState({ loading: false, data: null, loaded: true });
      });
    return () => { ignore = true; };
  }, [biginId]);

  useEffect(() => {
    if (!biginId || autoAuthRef.current) return;
    if (!leadState.loaded || leadState.loading) return;
    autoAuthRef.current = true;
    const lead = leadState.data;
    if (!lead) {
      dispatch(setAlertMessage('We could not retrieve your profile details from the link. Please fill in the required information.'));
      dispatch(setAlertType('warning'));
      return;
    }
    const nextToken = deriveTokenFromLead(lead);
    const nextUserId = deriveUserIdFromLead(lead);
    if (nextToken) {
      try { localStorage.setItem('BQS_TOKEN', nextToken); } catch { }
      try {
        if (nextUserId) localStorage.setItem('BQS__ID', nextUserId);
        localStorage.setItem('LOGIN_COUNT', '1');
        localStorage.setItem('PROFILE_COMPLETED', '0');
      } catch { }
      setJwt(nextToken);
    } else {
      try { localStorage.removeItem('BQS_TOKEN'); } catch { }
      try {
        if (nextUserId) localStorage.setItem('BQS__ID', nextUserId);
        localStorage.setItem('LOGIN_COUNT', '0');
        localStorage.setItem('PROFILE_COMPLETED', '0');
      } catch { }
      setJwt('');
    }
    if (nextUserId) setUserId(nextUserId);
  }, [biginId, leadState.loading, leadState.loaded, leadState.data, dispatch, navigate]);

  const profile = useSelector((s) => s.user.profile);
  const pic = useSelector((s) => s.user.profilePic);
  const { isStarted, value } = profile || {};
  const { profileData, email } = value || {};
  const storedUser = useMemo(() => value?.user || value?.data?.user || {}, [value]);
  const storedPlayerProfile = useMemo(
    () => storedUser?.playerProfile || value?.playerProfile || value?.data?.playerProfile || {},
    [storedUser, value]
  );
  const phone = useMemo(
    () => value?.phone || storedUser?.phone || value?.data?.phone || value?.data?.user?.phone || {},
    [value, storedUser]
  );

  const roleLabel = useMemo(() => {
    const lead = leadState.data || {};
    const leadProfile = lead.profile || {};
    const leadRole = lead.role || leadProfile.role || lead.profileRole;
    const leadRoleName =
      typeof leadRole === 'string'
        ? leadRole
        : leadRole?.roleName || leadRole?.name ||
        (Array.isArray(leadRole) && leadRole.length ? leadRole[0]?.roleName || leadRole[0]?.name : '');

    const userRoleSource =
      value?.role ||
      profileData?.role ||
      value?.userRole ||
      value?.roles ||
      storedUser.role ||
      storedUser.userRole ||
      storedPlayerProfile.role;
    let name = '';
    if (typeof userRoleSource === 'string') name = userRoleSource;
    else if (userRoleSource && typeof userRoleSource === 'object') {
      if (userRoleSource.roleName) name = userRoleSource.roleName;
      else if (userRoleSource.name) name = userRoleSource.name;
    } else if (Array.isArray(userRoleSource) && userRoleSource.length) {
      name = userRoleSource[0]?.roleName || userRoleSource[0]?.name || '';
    }

    return name || leadRoleName || 'Player';
  }, [value, profileData, storedUser, storedPlayerProfile, leadState.data]);

  const roleType = useMemo(() => {
    const lower = (roleLabel || '').toLowerCase();
    if (lower.includes('coach')) return 'coach';
    if (lower.includes('organ')) return 'organization';
    return 'player';
  }, [roleLabel]);

  const isCoach = roleType === 'coach';
  const isOrganization = roleType === 'organization';
  const isPlayer = roleType === 'player';

  useEffect(() => {
    if (showDemo) {
      const sampleOtp = '1234'.slice(0, OTP_LENGTH);
      setOtpState(createOtpState({ requested: true, otp: sampleOtp }));
      setTouched((prev) => ({ ...prev, otp: true }));
    }
  }, [showDemo]);

  const enableDemo = () => {
    setShowDemo(true);
    setForm((prev) => ({
      ...prev,
      fullname: prev.fullname || 'Alex Johnson',
      dob: prev.dob || '12-08-2000',
      gender: prev.gender || 'Male',
      phoneDisplay: prev.phoneDisplay || '+91 9876543210',
      phoneCc: prev.phoneCc || '+91',
      phoneNumber: prev.phoneNumber || '9876543210',
      email: prev.email || 'alex.johnson@example.com',
      preferredSport: prev.preferredSport || 'Badminton',
      exp: prev.exp || 'Intermediate',
    }));
    setTouched((prev) => ({ ...prev, fullname: true, dob: true, gender: true }));
  };

  const initial = useMemo(() => {
    const lead = leadState.data || {};
    const leadProfile = lead.profile || {};
    const leadGaming = leadProfile.gamingProfile || lead.gamingProfile || {};
    const leadPlayerProfile = lead.playerProfile || {};
    const leadCoachProfile = lead.coachProfile || leadProfile.coachProfile || {};
    const leadOrgProfile = lead.organizationProfile || lead.organisationProfile || leadProfile.organizationProfile || leadProfile.organisationProfile || {};
    const leadPlayerGaming = leadPlayerProfile.gamingProfile || {};
    const leadPlayerNameParts = splitFullName(leadPlayerProfile.fullName || '');
    const leadCoachNameParts = splitFullName(leadCoachProfile.fullName || '');
    const leadOrgContactNameParts = splitFullName(leadOrgProfile.contactName || '');
    const leadName = leadProfile.name || lead.name || {};
    const leadEmail = lead.email || leadProfile.email || leadProfile.organizationEmail || leadPlayerProfile.email || leadCoachProfile.email || leadOrgProfile.email || '';
    const leadPhone =
      lead.phone ||
      leadProfile.phone ||
      leadProfile.contactNumber ||
      lead.contactNumber ||
      leadProfile.mobile ||
      lead.mobile ||
      leadPlayerProfile.phone ||
      {};
    const leadOrgName =
      leadProfile.organizationName ||
      lead.organizationName ||
      leadProfile.companyName ||
      lead.companyName ||
      leadProfile.businessName ||
      lead.businessName ||
      leadPlayerProfile.organizationName ||
      leadCoachProfile.organizationName ||
      leadOrgProfile.organizationName ||
      '';
    const leadPreferredSport =
      leadProfile.preferredSport ||
      lead.preferredSport ||
      leadGaming.preferredSport ||
      leadProfile.primarySport ||
      lead.primarySport ||
      leadPlayerProfile.preferredSport ||
      leadCoachProfile.preferredSport ||
      leadOrgProfile.preferredSport ||
      '';
    const leadLocation = leadProfile.location || lead.location || leadProfile.city || lead.city || leadProfile.region || lead.region || leadProfile.state || lead.state || leadCoachProfile.location || leadOrgProfile.location || '';
    const leadContactName = leadProfile.contactName || lead.contactName || leadProfile.primaryContact || lead.primaryContact || leadProfile.contactPerson || lead.contactPerson || leadOrgProfile.contactName || '';
    const leadWebsite = leadProfile.website || lead.website || leadProfile.webSite || lead.webSite || leadProfile.websiteUrl || lead.websiteUrl || leadOrgProfile.website || '';
    const leadDob = leadProfile.dob || lead.dob || leadProfile.dateOfBirth || lead.dateOfBirth || leadPlayerProfile.dob || '';
    const leadHeight = leadProfile.height || lead.height || leadProfile.profileHeight || lead.profileHeight || leadPlayerProfile.height || '';
    const leadWeight = leadProfile.weight || lead.weight || leadProfile.profileWeight || lead.profileWeight || leadPlayerProfile.weight || '';
    const leadGender = leadProfile.gender || lead.gender || leadProfile.profileGender || lead.profileGender || leadPlayerProfile.gender || '';

    const apiProfile = profileData || {};
    const apiName = apiProfile.name || {};
    const apiGaming = apiProfile.gamingProfile || {};
    const apiEmailObj = email && typeof email === 'object' ? email : {};
    const apiEmailString = typeof email === 'string' ? email : '';
    const apiPhone = phone || {};
    const apiPlayer = storedPlayerProfile || {};
    const apiPlayerName = apiPlayer.name || {};
    const apiPlayerGaming = apiPlayer.gamingProfile || {};

    const nameFirst =
      apiName.firstName ||
      apiPlayerName.firstName ||
      storedUser.firstName ||
      leadName.firstName ||
      leadPlayerNameParts.firstName ||
      leadCoachNameParts.firstName ||
      leadOrgContactNameParts.firstName;
    const nameMiddle =
      apiName.middleName ||
      apiPlayerName.middleName ||
      storedUser.middleName ||
      leadName.middleName ||
      leadPlayerNameParts.middleName ||
      leadCoachNameParts.middleName ||
      leadOrgContactNameParts.middleName;
    const nameLast =
      apiName.lastName ||
      apiPlayerName.lastName ||
      storedUser.lastName ||
      leadName.lastName ||
      leadPlayerNameParts.lastName ||
      leadCoachNameParts.lastName ||
      leadOrgContactNameParts.lastName;

    const derivedFull = buildFullName(nameFirst, nameMiddle, nameLast);
    const fallbackFull =
      apiPlayer.fullName ||
      apiProfile.fullName ||
      storedUser.fullName ||
      leadPlayerProfile.fullName ||
      leadCoachProfile.fullName ||
      leadProfile.fullName ||
      lead.fullName ||
      '';

    const ccValue =
      apiPhone?.cc ||
      apiPhone?.countryCode ||
      leadPhone.cc ||
      leadPhone.countryCode ||
      '';
    const numberValue =
      apiPhone?.number ??
      apiPhone?.phoneNumber ??
      leadPhone.number ??
      leadPhone.phoneNumber ??
      '';
    const phoneCc = toText(ccValue).trim();
    const phoneNumber = toText(numberValue).trim();
    const phoneDisplay = phoneNumber ? `${phoneCc ? `${phoneCc} ` : ''}${phoneNumber}`.trim() : '';

    const preferredSport =
      leadPreferredSport ||
      apiProfile?.preferredSport ||
      apiGaming?.preferredSport ||
      apiProfile?.primarySport ||
      apiPlayer.preferredSport ||
      apiPlayerGaming?.preferredSport ||
      value?.preferredSport ||
      value?.profile?.preferredSport ||
      '';

    const expCandidates = [
      leadGaming.exp,
      leadGaming.experienceYears,
      leadPlayerGaming.exp,
      leadPlayerGaming.experienceYears,
      leadCoachProfile.experienceYears,
      apiGaming?.exp,
      apiGaming?.experienceYears,
      apiProfile?.experienceYears,
      apiPlayer.experienceYears,
      apiPlayer.exp,
    ];
    let rawExp = '';
    for (const candidate of expCandidates) {
      if (candidate !== undefined && candidate !== null) {
        if (typeof candidate === 'string') {
          if (candidate.trim()) {
            rawExp = candidate;
            break;
          }
        } else {
          rawExp = candidate;
          break;
        }
      }
    }
    const expText = toText(rawExp);

    const resolvedAreas =
      leadGaming.areasOfImprovement ||
      leadPlayerGaming.areasOfImprovement ||
      apiGaming?.areasOfImprovement ||
      apiPlayer.areasOfImprovement ||
      apiPlayerGaming?.areasOfImprovement ||
      '';
    const finalAreasArray = normalizeAreas(resolvedAreas);

    const finalCoach =
      leadGaming.coachName ||
      leadPlayerGaming.coachName ||
      apiGaming?.coachName ||
      apiPlayer.coachName ||
      '';
    const finalAcademy =
      leadGaming.academyName ||
      leadPlayerGaming.academyName ||
      apiGaming?.academyName ||
      apiPlayer.academyName ||
      '';
    const finalGoal =
      leadGaming.goal ||
      leadPlayerGaming.goal ||
      apiGaming?.goal ||
      apiPlayer.goal ||
      '';
    const finalInjury =
      leadGaming.injurySummary ||
      leadPlayerGaming.injurySummary ||
      apiGaming?.injurySummary ||
      apiPlayer.injurySummary ||
      '';

    const dobCandidate =
      normalizeDob(leadDob) ||
      normalizeDob(apiProfile?.dob) ||
      normalizeDob(apiPlayer.dob) ||
      normalizeDob(storedUser.dob);
    const finalDob = dobCandidate || '';

    const genderCandidate =
      leadGender ||
      apiProfile?.gender ||
      apiPlayer.gender ||
      storedUser.gender ||
      '';
    const finalGender = normalizeGender(genderCandidate);

    const heightCandidate =
      leadHeight ||
      apiProfile?.height ||
      apiPlayer.height ||
      '';
    const finalHeight = toText(heightCandidate);

    const weightCandidate =
      leadWeight ||
      apiProfile?.weight ||
      apiPlayer.weight ||
      '';
    const finalWeight = toText(weightCandidate);

    const emailValue =
      leadEmail ||
      apiEmailObj.email ||
      apiEmailString ||
      apiProfile?.email ||
      apiPlayer.email ||
      leadPlayerProfile.email ||
      storedUser.email ||
      '';

    return {
      fullname: derivedFull || fallbackFull,
      dob: finalDob,
      gender: finalGender,
      height: finalHeight,
      weight: finalWeight,
      injurySummary: finalInjury,
      email: emailValue,
      phoneDisplay,
      phoneCc,
      phoneNumber,
      preferredSport,
      exp: expText,
      areasText: finalAreasArray.join(', '),
      coachName: finalCoach,
      academyName: finalAcademy,
      goal: finalGoal,
      organizationName:
        leadOrgName ||
        apiProfile?.organizationName ||
        value?.profile?.organizationName ||
        storedUser.organizationName ||
        '',
      contactName:
        leadContactName ||
        apiProfile?.contactName ||
        value?.profile?.contactName ||
        storedUser.contactName ||
        '',
      location:
        leadLocation ||
        apiProfile?.location ||
        value?.profile?.location ||
        storedUser.location ||
        '',
      experienceYears: expText,
      website:
        leadWebsite ||
        apiProfile?.website ||
        value?.profile?.website ||
        storedUser.website ||
        '',
    };
  }, [profileData, email, phone, value, leadState.data, storedPlayerProfile, storedUser]);

  const [form, setForm] = useState(initial);

  useEffect(() => {
    if (showDemo) {
      setForm((prev) => ({
        ...prev,
        fullname: prev.fullname || 'Alex Johnson',
        dob: prev.dob || '12-08-2000',
        gender: prev.gender || 'Male',
        phoneDisplay: prev.phoneDisplay || '+91 9876543210',
        phoneCc: prev.phoneCc || '+91',
        phoneNumber: prev.phoneNumber || '9876543210',
        email: prev.email || 'alex.johnson@example.com',
        preferredSport: prev.preferredSport || 'Badminton',
        exp: prev.exp || 'Intermediate',
      }));
    }
  }, [showDemo]);
  const [touched, setTouched] = useState({});
  const [saving, setSaving] = useState(false);
  const [otpState, setOtpState] = useState(() => createOtpState());
  const clickedRef = useRef(false);

  useEffect(() => {
    if (initial && Object.keys(initial).length > 0) {
      setForm(prev => {
        // Only override if previous form was empty or we are first-loading lead data
        if (!prev.email || !leadState.loaded) return { ...initial };
        return prev;
      });
      if (!leadState.loaded && leadState.data) {
        setLeadState(ps => ({ ...ps, loaded: true }));
      }
    }
  }, [initial, leadState.loaded, leadState.data]);

  const reqErrors = useMemo(() => {
    const e = {};
    if (!form.email) e.email = 'Email is required';
    else if (!emailRegex.test(form.email)) e.email = 'Enter a valid email address';

    if (isPlayer) {
      const { firstName } = splitFullName(form.fullname);
      if (!form.fullname || !firstName) e.fullname = 'First name is required';
      if (!form.dob) e.dob = 'Date of Birth is required';
      else if (!/^\d{2}-\d{2}-\d{4}$/.test(form.dob)) e.dob = 'Use DD-MM-YYYY format';
      else if (!validDOB(form.dob)) e.dob = 'Enter a valid past date';
      if (!form.gender) e.gender = 'Gender is required';
      if (!form.height) e.height = 'Height is required';
      if (!form.weight) e.weight = 'Weight is required';
    } else if (isCoach) {
      const { firstName } = splitFullName(form.fullname);
      if (!form.fullname || !firstName) e.fullname = 'First name is required';
      if (!form.preferredSport) e.preferredSport = 'Preferred sport is required';
      if (!form.organizationName) e.organizationName = 'Organization name is required';
      if (!form.location) e.location = 'Location is required';
      if (!form.experienceYears) e.experienceYears = 'Experience is required';
    } else if (isOrganization) {
      if (!form.organizationName) e.organizationName = 'Organization name is required';
      if (!form.contactName) e.contactName = 'Contact name is required';
      if (!form.preferredSport) e.preferredSport = 'Preferred sport is required';
      if (!form.location) e.location = 'Location is required';
    }
    return e;
  }, [form, isCoach, isOrganization, isPlayer]);

  const requiredOK = Object.keys(reqErrors).length === 0;

  const locks = useMemo(() => {
    if (!leadState.data) return {};
    const lead = leadState.data || {};

    return {
      fullname: false,
      dob: false,
      gender: false,
      height: false,
      weight: false,
      email: Boolean(initial.email),
      preferredSport: true,
      organizationName: false,
      contactName: false,
      location: false,
      experienceYears: false,
      exp: false,
      academyName: false,
      coachName: false,
      areasText: false,
      goal: false,
      injurySummary: false,
      website: false,
      phoneDisplay: true,
    };
  }, [initial, isPlayer, leadState.data]);

  // Gating banner: show when first login or computed completeness is false
  const shouldGate = useMemo(() => {
    let loginCount = 0;
    let completedFlag = false;
    try {
      loginCount = parseInt(localStorage.getItem('LOGIN_COUNT') || '0', 10) || 0;
      completedFlag = localStorage.getItem('PROFILE_COMPLETED') === '1';
    } catch { }
    const nameObj = profileData?.name || {};
    const hasName = Boolean(nameObj?.firstName && nameObj?.lastName);
    let dobValid = false;
    if (profileData?.dob && /^\d{2}-\d{2}-\d{4}$/.test(profileData?.dob)) {
      const dt = DateTime.fromFormat(profileData?.dob, 'dd-MM-yyyy');
      if (dt.isValid && dt <= DateTime.now().startOf('day')) dobValid = true;
    }
    const isComplete = Boolean(email?.email && hasName && profileData?.gender && dobValid);
    return !completedFlag && (loginCount <= 1 || !isComplete);
  }, [profileData, email]);

  const handleOtpChange = (index, value) => {
    const cleaned = (value || '').replace(/\D/g, '');
    setOtpState((prev) => {
      const chars = prev.otp.split('').slice(0, OTP_LENGTH);
      while (chars.length < OTP_LENGTH) chars.push('');
      if (cleaned.length > 1) {
        cleaned
          .slice(0, OTP_LENGTH - index)
          .split('')
          .forEach((char, offset) => {
            const target = index + offset;
            if (target < OTP_LENGTH) chars[target] = char;
          });
      } else {
        chars[index] = cleaned || '';
      }
      return { ...prev, otp: chars.join(''), error: '' };
    });
    if (cleaned.length > 1) {
      const nextIndex = Math.min(index + cleaned.length - 1, OTP_LENGTH - 1);
      otpInputRefs.current[nextIndex]?.focus();
      return;
    }
    if (cleaned && index < OTP_LENGTH - 1) otpInputRefs.current[index + 1]?.focus();
    if (!cleaned && index > 0) otpInputRefs.current[index]?.value || otpInputRefs.current[index - 1]?.focus();
  };

  const handleOtpKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !otpState.otp[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowLeft' && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
      e.preventDefault();
    }
    if (e.key === 'ArrowRight' && index < OTP_LENGTH - 1) {
      otpInputRefs.current[index + 1]?.focus();
      e.preventDefault();
    }
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    if (name === 'otp') {
      return;
    }
    if (locks[name]) return;
    setForm((p) => ({ ...p, [name]: value }));
  };
  const onBlur = (e) => { const { name } = e.target; if (name !== 'otp') setTouched((p) => ({ ...p, [name]: true })); };
  const showErr = (k) => Boolean((touched[k] || clickedRef.current) && reqErrors[k]);

  const requestOtp = async () => {
    const trimmedNumber = (form.phoneNumber || '').trim();
    if (!trimmedNumber) {
      setOtpState((prev) => ({ ...prev, error: 'Phone number not available. Please contact support.' }));
      return;
    }
    try {
      setOtpState(createOtpState({ requesting: true, registrationId: otpState.registrationId }));
      const res = await fetch(POST_OTP_REGISTRATION, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: userId,
          phone: {
            cc: (form.phoneCc || '+91').startsWith('+') ? form.phoneCc || '+91' : `+${form.phoneCc || '+91'}`,
            number: trimmedNumber,
          },
          role: roleType,
        }),
      });
      if (!res.ok) {
        throw new Error('Unable to send OTP. Please try again.');
      }
      const json = await res.json();
      if (!json || json.code !== 200) {
        throw new Error(json?.message || 'Unable to send OTP. Please try again.');
      }
      setOtpState(createOtpState({ requested: true, registrationId: json?.data?._id || json?.data?.registrationId || '' }));
      try { dispatch(setAlertMessage('OTP sent successfully. Please enter the code.')); dispatch(setAlertType('success')); } catch { }
    } catch (error) {
      setOtpState((prev) => ({ ...prev, requesting: false, requested: false, error: error?.message || 'Unable to send OTP. Please try again.', otp: '' }));
      dispatch(setAlertMessage(error?.message || 'Unable to send OTP. Please try again.'));
      dispatch(setAlertType('error'));
    }
  };

  const callSignupCompleteApi = async () => {
    try {
      const payload = {
        phone: {
          cc: (form.phoneCc || '+91').startsWith('+') ? form.phoneCc || '+91' : `+${form.phoneCc || '+91'}`,
          number: form.phoneNumber || '',
        },
      };
      const res = await fetch(POST_SIGNUP_COMPLETE, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        console.warn('Signup complete API failed', res.statusText);
      }
    } catch (err) {
      console.error('Error calling signup complete API', err);
    }
  };

  const getProfilePayload = () => {
    const { firstName, middleName, lastName } = splitFullName(form.fullname || '');
    const areas = (form.areasText || '').split(',').map((s) => s.trim()).filter(Boolean);
    const heightValue = toNumberIfNumeric(form.height);
    const weightValue = toNumberIfNumeric(form.weight);
    const gamingProfilePayload = {
      exp: form.exp || '',
      goal: form.goal || '',
      injurySummary: form.injurySummary || '',
      areasOfImprovement: areas,
      areasOfImprovementOthers: '',
      preferredSport: form.preferredSport || '',
      acadyName: form.academyName || '',
      coach: form.coachName || '',
    };

    let body;
    if (isPlayer) {
      body = {
        email: form.email,
        profileData: {
          name: { firstName, middleName, lastName },
          dob: form.dob,
          gender: form.gender,
          weight: weightValue,
          height: heightValue,
          gamingProfile: gamingProfilePayload,
        },
      };
    } else if (isCoach) {
      body = {
        email: form.email,
        profileData: {
          name: { firstName, middleName, lastName },
          preferredSport: form.preferredSport,
          organizationName: form.organizationName,
          location: form.location,
          experienceYears: form.experienceYears,
        },
      };
    } else if (isOrganization) {
      body = {
        email: form.email,
        profileData: {
          organizationName: form.organizationName,
          contactName: form.contactName,
          preferredSport: form.preferredSport,
          location: form.location,
          website: form.website,
        },
      };
    } else {
      body = {
        email: form.email,
        profileData: {},
      };
    }
    return body;
  };

  const handleVerifyOtp = async () => {
    try {
      let otpPayload = (otpState.otp || '').trim();
      const effectiveOtp = otpPayload || otpInputRefs.current.map((input) => (input?.value || '').trim()).join('');
      if (!effectiveOtp || effectiveOtp.length < OTP_LENGTH) {
        setOtpState((prev) => ({ ...prev, error: 'Please enter the complete OTP.' }));
        return;
      }
      otpPayload = effectiveOtp;
      if (effectiveOtp !== otpState.otp) {
        setOtpState((prev) => ({ ...prev, otp: effectiveOtp }));
      }
      
      const registrationId = otpState.registrationId || userId;
      if (!registrationId) {
        setOtpState((prev) => ({ ...prev, error: 'Registration session expired. Please request OTP again.' }));
        return;
      }
      
      // UI ONLY verification
      setOtpState((prev) => ({ ...prev, verified: true, error: '' }));
      try { dispatch(setAlertMessage('OTP entered. Please save to confirm.')); dispatch(setAlertType('success')); } catch { }
    } catch (error) {
      setOtpState((prev) => ({ ...prev, verifying: false, error: error?.message || 'Invalid OTP. Please try again.' }));
    }
  };

  const handleSave = async () => {
    clickedRef.current = true;
    if (!requiredOK) return;
    if (!jwt && !biginId) {
      dispatch(setAlertMessage('Session expired. Please login again.'));
      dispatch(setAlertType('error'));
      navigate('/');
      return;
    }

    if (!biginId && leadState.data) {
      dispatch(setAlertMessage('Profile data already submitted. Please contact support for changes.'));
      dispatch(setAlertType('error'));
      return;
    }

    const body = getProfilePayload();

    setSaving(true);
    try {
      if (otpState.requested && !otpState.backendVerified) {
        let otpPayload = (otpState.otp || '').trim();
        const effectiveOtp = otpPayload || otpInputRefs.current.map((input) => (input?.value || '').trim()).join('');
        const registrationId = otpState.registrationId || userId;
        
        if (!registrationId) {
          throw new Error('Registration session expired. Please request OTP again.');
        }

        const registerHeaders = { 'Content-Type': 'application/json' };
        if (jwt) registerHeaders.Authorization = `Bearer ${jwt}`;

        const { firstName, lastName } = splitFullName(form.fullname || form.contactName || '');
        const res = await fetch(`${POST_REGISTER}/${registrationId}`, {
          method: 'POST',
          headers: registerHeaders,
          body: JSON.stringify({
            otpToken: {
              type: 'registration',
              otp: effectiveOtp,
            },
            profileData: {
              name: {
                firstName: firstName || 'John',
                lastName: lastName || 'Doe',
              },
            },
          }),
        });
        
        if (!res.ok) {
          setOtpState((prev) => ({ ...prev, verified: false, otp: '', error: 'Invalid OTP. Please check and try again.' }));
          setTimeout(() => otpInputRefs.current[0]?.focus(), 100);
          throw new Error('Invalid OTP. Please check and try again.');
        }
        const json = await res.json();
        if (!json || json.code !== 200) {
          setOtpState((prev) => ({ ...prev, verified: false, otp: '', error: json?.message || 'Invalid OTP. Please try again.' }));
          setTimeout(() => otpInputRefs.current[0]?.focus(), 100);
          throw new Error(json?.message || 'Invalid OTP. Please try again.');
        }

        const newJwt = json?.data?.accessToken || json?.data?.jwt || json?.data?.token || jwt;
        const newUserId = json?.data?.userId || json?.data?.id || json?.data?._id || userId;

        if (newJwt && newJwt !== jwt) {
          try { localStorage.setItem('BQS_TOKEN', newJwt); } catch { }
          setJwt(newJwt);
        }
        if (newUserId && newUserId !== userId) {
          try { localStorage.setItem('BQS__ID', newUserId); } catch { }
          setUserId(newUserId);
        }
        
        setOtpState((prev) => ({ ...prev, backendVerified: true, registrationId: json?.data?._id || prev.registrationId }));
        
        await callSignupCompleteApi();
      }

      if (jwt) {
        const updated = await dispatch(UpdateUserProfile(jwt, body));
        if (!updated || !updated.success) {
          throw new Error(updated?.message || 'Unable to update profile.');
        }
      }

      try { localStorage.setItem('PROFILE_COMPLETED', '1'); } catch { }
      try { sessionStorage.removeItem('ALLOW_PROFILE_UPDATE'); } catch { }
      navigate('/');
      dispatch(setIsLoginDialog(true));
    } catch (error) {
      setOtpState((prev) => ({ ...prev, verifying: false }));
      dispatch(setAlertMessage(error?.message || 'Unable to save profile. Please try again.'));
      dispatch(setAlertType('error'));
    } finally {
      setSaving(false);
    }
  };

  const loading = isStarted && !value;

  // ── Gate OTP handlers ───────────────────────────────────────────────────────
  const handleGateOtpChange = (index, value) => {
    const cleaned = (value || '').replace(/\D/g, '');
    setGateOtpState((prev) => {
      const chars = prev.otp.split('').slice(0, OTP_LENGTH);
      while (chars.length < OTP_LENGTH) chars.push('');
      if (cleaned.length > 1) {
        cleaned.slice(0, OTP_LENGTH - index).split('').forEach((char, offset) => {
          const target = index + offset;
          if (target < OTP_LENGTH) chars[target] = char;
        });
      } else {
        chars[index] = cleaned || '';
      }
      return { ...prev, otp: chars.join(''), error: '' };
    });
    if (cleaned.length > 1) {
      const nextIndex = Math.min(index + cleaned.length - 1, OTP_LENGTH - 1);
      gateOtpInputRefs.current[nextIndex]?.focus();
      return;
    }
    if (cleaned && index < OTP_LENGTH - 1) gateOtpInputRefs.current[index + 1]?.focus();
    if (!cleaned && index > 0) gateOtpInputRefs.current[index - 1]?.focus();
  };

  const handleGateOtpKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !gateOtpState.otp[index] && index > 0) gateOtpInputRefs.current[index - 1]?.focus();
    if (e.key === 'ArrowLeft' && index > 0) { gateOtpInputRefs.current[index - 1]?.focus(); e.preventDefault(); }
    if (e.key === 'ArrowRight' && index < OTP_LENGTH - 1) { gateOtpInputRefs.current[index + 1]?.focus(); e.preventDefault(); }
  };

  const requestGateOtp = async () => {
    const trimmedNumber = (form.phoneNumber || '').trim();
    if (!trimmedNumber) {
      setGateOtpState((prev) => ({ ...prev, error: 'Phone number not available. Please contact support.' }));
      return;
    }

    try {
      setGateOtpState(createOtpState({ requesting: true, registrationId: gateOtpState.registrationId }));
      const res = await fetch(POST_OTP_REGISTRATION, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: userId,
          phone: {
            cc: (form.phoneCc || '+91').startsWith('+') ? form.phoneCc || '+91' : `+${form.phoneCc || '+91'}`,
            number: trimmedNumber,
          },
          role: roleType,
        }),
      });
      if (!res.ok) throw new Error('Unable to send OTP. Please try again.');
      const json = await res.json();
      if (!json || json.code !== 200) throw new Error(json?.message || 'Unable to send OTP. Please try again.');
      setGateOtpState(createOtpState({ requested: true, registrationId: json?.data?._id || json?.data?.registrationId || '' }));
      dispatch(setAlertMessage('OTP sent successfully. Please enter the code.'));
      dispatch(setAlertType('success'));
    } catch (error) {
      setGateOtpState((prev) => ({ ...prev, requesting: false, requested: false, error: error?.message || 'Unable to send OTP. Please try again.', otp: '' }));
      dispatch(setAlertMessage(error?.message || 'Unable to send OTP. Please try again.'));
      dispatch(setAlertType('error'));
    }
  };

  const handleGateVerifyOtp = async () => {
    const effectiveOtp = (gateOtpState.otp || '').trim() ||
      gateOtpInputRefs.current.map((el) => (el?.value || '').trim()).join('');
    if (!effectiveOtp || effectiveOtp.length < OTP_LENGTH) {
      setGateOtpState((prev) => ({ ...prev, error: 'Please enter the complete OTP.' }));
      return;
    }

    const registrationId = gateOtpState.registrationId || userId;
    if (!registrationId) {
      setGateOtpState((prev) => ({ ...prev, error: 'Registration session expired. Please request OTP again.' }));
      return;
    }
    setGateOtpState((prev) => ({ ...prev, verifying: true, error: '' }));
    try {
      const registerHeaders = { 'Content-Type': 'application/json' };
      if (jwt) registerHeaders.Authorization = `Bearer ${jwt}`;
      const { firstName, lastName } = splitFullName(form.fullname || form.contactName || '');
      const res = await fetch(`${POST_REGISTER}/${registrationId}`, {
        method: 'POST',
        headers: registerHeaders,
        body: JSON.stringify({
          otpToken: { type: 'registration', otp: effectiveOtp },
          profileData: {
            name: {
              firstName: firstName || 'John',
              lastName: lastName || 'Doe',
            },
          },
        }),
      });
      if (!res.ok) {
        setGateOtpState((prev) => ({ ...prev, verifying: false, otp: '', error: 'Invalid OTP. Please check and try again.' }));
        setTimeout(() => gateOtpInputRefs.current[0]?.focus(), 100);
        return;
      }
      const json = await res.json();
      if (!json || json.code !== 200) {
        setGateOtpState((prev) => ({ ...prev, verifying: false, otp: '', error: json?.message || 'Invalid OTP. Please try again.' }));
        setTimeout(() => gateOtpInputRefs.current[0]?.focus(), 100);
        return;
      }
      const newJwt = json?.data?.accessToken || json?.data?.jwt || json?.data?.token || jwt;
      const newUserId = json?.data?.userId || json?.data?.id || json?.data?._id || userId;
      if (newJwt && newJwt !== jwt) { try { localStorage.setItem('BQS_TOKEN', newJwt); } catch { } setJwt(newJwt); }
      if (newUserId && newUserId !== userId) { try { localStorage.setItem('BQS__ID', newUserId); } catch { } setUserId(newUserId); }
      await callSignupCompleteApi();
      setGateOtpState((prev) => ({ ...prev, verifying: false, verified: true }));
      setOtpState(createOtpState({ verified: true, backendVerified: true, registrationId: json?.data?._id || gateOtpState.registrationId }));
      setPhoneVerified(true);
      dispatch(setAlertMessage('Phone verified! Please complete your profile.'));
      dispatch(setAlertType('success'));
    } catch (error) {
      setGateOtpState((prev) => ({ ...prev, verifying: false, error: error?.message || 'Invalid OTP. Please try again.' }));
    }
  };
  // ── End Gate OTP handlers ────────────────────────────────────────────────────

  return (
    <div className="updateProfile_con relative overflow-hidden min-h-screen pt-24 pb-12 px-4 sm:px-6 lg:px-8" style={{ background: 'linear-gradient(135deg, #0a1f14 0%, #0d2818 50%, #051a0d 100%)' }}>
      {/* World-Class Animation Keyframes */}
      <style>{`
        @keyframes particleFloat {
          0%, 100% { transform: translate(0, 0); opacity: 0.3; }
          25% { transform: translate(30px, -50px); opacity: 0.8; }
          50% { transform: translate(-20px, -100px); opacity: 0.6; }
          75% { transform: translate(40px, -75px); opacity: 0.7; }
        }
        @keyframes auroraPulse {
          0%, 100% { opacity: 0.3; filter: blur(80px) hue-rotate(0deg); transform: scale(1) translateY(0); }
          33% { opacity: 0.5; filter: blur(100px) hue-rotate(30deg); transform: scale(1.2) translateY(-20px); }
          66% { opacity: 0.4; filter: blur(90px) hue-rotate(-30deg); transform: scale(1.1) translateY(20px); }
        }
        @keyframes meshShift {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(50px, 30px) scale(1.05); }
        }
        @keyframes nodeGlow {
          0%, 100% { opacity: 0.4; transform: scale(1); box-shadow: 0 0 20px rgba(204,255,0,0.3); }
          50% { opacity: 0.8; transform: scale(1.3); box-shadow: 0 0 40px rgba(204,255,0,0.6); }
        }
        @keyframes lineConnect {
          0% { stroke-dashoffset: 500; opacity: 0.2; }
          50% { opacity: 0.6; }
          100% { stroke-dashoffset: 0; opacity: 0.2; }
        }
        @keyframes radialBurst {
          0% { transform: scale(0.8) rotate(0deg); opacity: 0.2; }
          50% { transform: scale(1.2) rotate(180deg); opacity: 0.4; }
          100% { transform: scale(0.8) rotate(360deg); opacity: 0.2; }
        }
        @keyframes depthLayer {
          0%, 100% { transform: translateZ(0) translateY(0); opacity: 0.15; }
          50% { transform: translateZ(50px) translateY(-30px); opacity: 0.3; }
        }
      `}</style>

      {/* Epic Background Layers */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ perspective: '1000px' }}>
        <div className="absolute -top-1/2 -right-1/4 w-[900px] h-[900px] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(16,185,129,0.4) 0%, rgba(5,150,105,0.2) 50%, transparent 70%)', animation: 'auroraPulse 15s ease-in-out infinite', filter: 'blur(80px)' }} />
        <div className="absolute top-1/4 -left-1/4 w-[800px] h-[800px] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(52,211,153,0.35) 0%, rgba(16,185,129,0.15) 50%, transparent 70%)', animation: 'auroraPulse 18s ease-in-out infinite 5s', filter: 'blur(90px)' }} />
        <div className="absolute bottom-1/4 right-1/3 w-[700px] h-[700px] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(110,231,183,0.3) 0%, rgba(52,211,153,0.12) 50%, transparent 70%)', animation: 'auroraPulse 20s ease-in-out infinite 10s', filter: 'blur(100px)' }} />

        {/* Animated Mesh Grid */}
        <div className="absolute inset-0 opacity-10" style={{ animation: 'meshShift 40s ease-in-out infinite' }}>
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="meshGrid" x="0" y="0" width="80" height="80" patternUnits="userSpaceOnUse">
                <circle cx="40" cy="40" r="1.5" fill="#ccff00" opacity="0.6" />
                <line x1="40" y1="0" x2="40" y2="80" stroke="#ccff00" strokeWidth="0.5" opacity="0.3" />
                <line x1="0" y1="40" x2="80" y2="40" stroke="#ccff00" strokeWidth="0.5" opacity="0.3" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#meshGrid)" />
          </svg>
        </div>

        {/* Particles and other animation layers same as Signup */}
        {[...Array(12)].map((_, i) => (
          <div key={`particle-${i}`} className="absolute w-3 h-3 rounded-full"
            style={{ left: `${15 + (i * 7)}%`, top: `${20 + (i * 5) % 60}%`, background: 'radial-gradient(circle, #ccff00 0%, rgba(204,255,0,0.3) 70%, transparent 100%)', animation: `particleFloat ${8 + i * 2}s ease-in-out infinite ${i * 0.5}s`, boxShadow: '0 0 20px rgba(204,255,0,0.5)' }} />
        ))}
      </div>

      <div className="max-w-4xl mx-auto relative z-10">

        {/* ── PHONE VERIFICATION GATE (biginId link flow) ── */}
        {biginId && !phoneVerified ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
            className="min-h-[80vh] flex flex-col items-center justify-center px-4"
          >
            {/* Brand mark */}
            <p className="text-emerald-400 text-xs font-bold uppercase tracking-[0.3em] mb-10">Visist · Account Verification</p>

            {/* Headline */}
            <h1 className="text-4xl font-black text-white text-center mb-3" style={{ letterSpacing: '-0.02em' }}>
              Verify your number
            </h1>
            <p className="text-white/40 text-base text-center mb-10 max-w-xs">
              We'll send a one-time code to confirm your identity.
            </p>

            {/* Phone number pill + Send OTP inline row */}
            {leadState.loading ? (
              <div className="mb-10"><CircularProgress style={{ color: '#4ade80' }} size={24} /></div>
            ) : (
              <div className="flex items-center gap-3 mb-10">
                {/* Phone pill */}
                <div className="flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/10 bg-white/5">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.15 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.06 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 21 16.92z" />
                  </svg>
                  <span className="text-white font-semibold text-sm tracking-wide">
                    {form.phoneDisplay || (form.phoneNumber ? `${form.phoneCc || ''} ${form.phoneNumber}`.trim() : '—')}
                  </span>
                </div>

                {/* Inline Send OTP button (only before OTP is requested) */}
                {!gateOtpState.requested && (
                  <button
                    id="verify-phone-btn"
                    type="button"
                    onClick={requestGateOtp}
                    disabled={gateOtpState.requesting || !form.phoneNumber}
                    className="px-4 py-2.5 rounded-full font-semibold text-sm text-white transition-all duration-200 hover:-translate-y-0.5 active:scale-[0.97] disabled:opacity-40 whitespace-nowrap"
                    style={{ background: '#247E45', boxShadow: '0 3px 12px rgba(36,126,69,0.4)' }}
                  >
                    {gateOtpState.requesting ? 'Sending…' : 'Send OTP'}
                  </button>
                )}
              </div>
            )}

            {/* Error below the row */}
            {gateOtpState.error && !gateOtpState.requested && (
              <p className="text-red-400 text-xs -mt-7 mb-8 font-semibold text-center">{gateOtpState.error}</p>
            )}

            {/* Blocked State */}
            {isBlocked && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="w-full max-w-sm mt-8 p-6 rounded-3xl border border-red-500/30 bg-red-500/5 text-center"
              >
                <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4 border border-red-500/40">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#f87171" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                </div>
                <h3 className="text-white font-bold text-lg mb-2">Account Blocked</h3>
                <p className="text-white/60 text-sm leading-relaxed mb-4">
                  Too many failed attempts. Please contact our support team to reactivate your account.
                </p>
                <a
                  href={`https://mail.google.com/mail/?view=cm&fs=1&to=support@visist.ai&su=Account%20Reactivation%20Request&body=Hi%20Visist%20Support,%0D%0A%0D%0AMy%20account%20has%20been%20blocked%20after%20multiple%20failed%20OTP%20attempts.%20Please%20help%20me%20reactivate%20it.%0D%0A%0D%0ABigin%20ID:%20${biginId}%0D%0APhone:%20${form.phoneDisplay || form.phoneNumber}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  referrerPolicy="no-referrer"
                  className="inline-block px-6 py-2.5 bg-white/10 hover:bg-white/20 text-white font-bold rounded-full transition-all border border-white/10"
                >
                  support@visist.ai
                </a>
              </motion.div>
            )}

            {/* Step 2 — OTP boxes */}
            {gateOtpState.requested && !isBlocked && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="w-full max-w-xs flex flex-col items-center"
              >
                <p className="text-white/40 text-xs font-semibold mb-5 tracking-widest uppercase">Enter 4-digit code</p>
                <div className="flex gap-3 mb-6">
                  {Array.from({ length: OTP_LENGTH }).map((_, idx) => (
                    <input
                      key={idx}
                      ref={(el) => (gateOtpInputRefs.current[idx] = el)}
                      className={`w-14 h-14 text-center text-2xl font-black rounded-xl text-white outline-none transition-all border-2 bg-white/5 ${
                        gateOtpState.error
                          ? 'border-red-500/60'
                          : 'border-white/15 focus:border-emerald-400'
                      }`}
                      type="password"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={idx === 0 ? OTP_LENGTH : 1}
                      value={(gateOtpState.otp || '')[idx] || ''}
                      onChange={(e) => handleGateOtpChange(idx, e.target.value)}
                      onKeyDown={(e) => handleGateOtpKeyDown(e, idx)}
                    />
                  ))}
                </div>

                {gateOtpState.error && (
                  <p className="text-red-400 text-xs mb-4 font-semibold text-center">{gateOtpState.error}</p>
                )}

                <button
                  id="submit-gate-otp-btn"
                  type="button"
                  onClick={handleGateVerifyOtp}
                  disabled={gateOtpState.verifying || gateOtpState.otp.replace(/\D/g, '').length < OTP_LENGTH}
                  className="w-full py-4 rounded-2xl font-bold text-base text-white transition-all duration-300 hover:-translate-y-0.5 active:scale-[0.98] disabled:opacity-40 mb-4"
                  style={{ background: '#247E45', boxShadow: '0 4px 20px rgba(36,126,69,0.35)' }}
                >
                  {gateOtpState.verifying ? 'Verifying…' : 'Verify & Continue'}
                </button>

                <button
                  type="button"
                  onClick={requestGateOtp}
                  disabled={gateOtpState.requesting}
                  className="text-white/30 hover:text-white/60 text-sm transition-colors disabled:opacity-20"
                >
                  {gateOtpState.requesting ? 'Sending…' : 'Resend code'}
                </button>
              </motion.div>
            )}
          </motion.div>
        ) : (
          /* ── FULL PROFILE FORM ── */
          <>
        {shouldGate && (
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-6 p-4 bg-emerald-500/20 border border-emerald-500/30 rounded-2xl text-emerald-200 text-center font-bold backdrop-blur-md" role="status">
            Please complete your profile to finish the Sign Up process.
          </motion.div>
        )}

        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-4xl md:text-5xl font-black text-white tracking-tight uppercase" data-testid="update-profile-heading" style={{ textShadow: '0 4px 20px rgba(0,0,0,0.3)' }}>
              Complete Your Profile
            </h1>
            <p className="text-white/60 mt-2 text-lg">Help us tailor your VISIST experience</p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <CircularProgress style={{ color: '#ccff00' }} />
          </div>
        ) : (
          <div className="space-y-8">
            <div className="backdrop-blur-xl rounded-3xl p-6 md:p-10 border border-white/10 shadow-2xl" style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 100%)' }}>
              <div className="flex items-center justify-between mb-8 border-b border-white/10 pb-6">
                <h2 className="text-2xl font-black text-white">Personal Details</h2>
                <span className="px-4 py-1.5 rounded-full bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 font-bold text-sm uppercase tracking-wider">{roleLabel}</span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                {isPlayer && (
                  <>
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-white/80 ml-1">Full Name {!form.fullname && <span className="text-red-400 text-xs ml-2 uppercase font-black">Required</span>}</label>
                      <input name="fullname" type="text" value={form.fullname} onChange={onChange} onBlur={onBlur} disabled={locks.fullname} className="w-full px-5 py-3.5 bg-white/10 border border-white/20 rounded-2xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-400 outline-none transition-all text-white placeholder-white/30 backdrop-blur-sm disabled:opacity-50" />
                      {showErr('fullname') && <p className="text-red-400 text-xs mt-1 font-bold">{reqErrors.fullname}</p>}
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-bold text-white/80 ml-1">Date of Birth {!form.dob && <span className="text-red-400 text-xs ml-2 uppercase font-black">Required</span>}</label>
                      <input name="dob" type="text" placeholder="DD-MM-YYYY" value={form.dob} onChange={onChange} onBlur={onBlur} disabled={locks.dob} className="w-full px-5 py-3.5 bg-white/10 border border-white/20 rounded-2xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-400 outline-none transition-all text-white placeholder-white/30 backdrop-blur-sm disabled:opacity-50" />
                      {showErr('dob') && <p className="text-red-400 text-xs mt-1 font-bold">{reqErrors.dob}</p>}
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-bold text-white/80 ml-1">Height (cm) {!form.height && <span className="text-red-400 text-xs ml-2 uppercase font-black">Required</span>}</label>
                      <input name="height" type="text" value={form.height} onChange={onChange} onBlur={onBlur} disabled={locks.height} className="w-full px-5 py-3.5 bg-white/10 border border-white/20 rounded-2xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-400 outline-none transition-all text-white placeholder-white/30 backdrop-blur-sm disabled:opacity-50" />
                      {showErr('height') && <p className="text-red-400 text-xs mt-1 font-bold">{reqErrors.height}</p>}
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-bold text-white/80 ml-1">Weight (kg) {!form.weight && <span className="text-red-400 text-xs ml-2 uppercase font-black">Required</span>}</label>
                      <input name="weight" type="text" value={form.weight} onChange={onChange} onBlur={onBlur} disabled={locks.weight} className="w-full px-5 py-3.5 bg-white/10 border border-white/20 rounded-2xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-400 outline-none transition-all text-white placeholder-white/30 backdrop-blur-sm disabled:opacity-50" />
                      {showErr('weight') && <p className="text-red-400 text-xs mt-1 font-bold">{reqErrors.weight}</p>}
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-bold text-white/80 ml-1">Gender {!form.gender && <span className="text-red-400 text-xs ml-2 uppercase font-black">Required</span>}</label>
                      <select name="gender" value={form.gender} onChange={onChange} onBlur={onBlur} disabled={locks.gender} className="w-full px-5 py-3.5 bg-white/10 border border-white/20 rounded-2xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-400 outline-none transition-all text-white backdrop-blur-sm disabled:opacity-50 appearance-none">
                        <option value="" className="bg-gray-900">Select gender</option>
                        <option value="Male" className="bg-gray-900">Male</option>
                        <option value="Female" className="bg-gray-900">Female</option>
                        <option value="Other" className="bg-gray-900">Other</option>
                      </select>
                      {showErr('gender') && <p className="text-red-400 text-xs mt-1 font-bold">{reqErrors.gender}</p>}
                    </div>
                  </>
                )}

                {isCoach && (
                  <>
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-white/80 ml-1">Full Name {!form.fullname && <span className="text-red-400 text-xs ml-2 uppercase font-black">Required</span>}</label>
                      <input name="fullname" type="text" value={form.fullname} onChange={onChange} onBlur={onBlur} disabled={locks.fullname} className="w-full px-5 py-3.5 bg-white/10 border border-white/20 rounded-2xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-400 outline-none transition-all text-white backdrop-blur-sm" />
                      {showErr('fullname') && <p className="text-red-400 text-xs mt-1 font-bold">{reqErrors.fullname}</p>}
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-white/80 ml-1">Organization {!form.organizationName && <span className="text-red-400 text-xs ml-2 uppercase font-black">Required</span>}</label>
                      <input name="organizationName" type="text" value={form.organizationName} onChange={onChange} onBlur={onBlur} disabled={locks.organizationName} className="w-full px-5 py-3.5 bg-white/10 border border-white/20 rounded-2xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-400 outline-none transition-all text-white backdrop-blur-sm" />
                      {showErr('organizationName') && <p className="text-red-400 text-xs mt-1 font-bold">{reqErrors.organizationName}</p>}
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-white/80 ml-1">Location {!form.location && <span className="text-red-400 text-xs ml-2 uppercase font-black">Required</span>}</label>
                      <input name="location" type="text" value={form.location} onChange={onChange} onBlur={onBlur} disabled={locks.location} className="w-full px-5 py-3.5 bg-white/10 border border-white/20 rounded-2xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-400 outline-none transition-all text-white backdrop-blur-sm" />
                      {showErr('location') && <p className="text-red-400 text-xs mt-1 font-bold">{reqErrors.location}</p>}
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-white/80 ml-1">Experience (Years) {!form.experienceYears && <span className="text-red-400 text-xs ml-2 uppercase font-black">Required</span>}</label>
                      <input name="experienceYears" type="text" value={form.experienceYears} onChange={onChange} onBlur={onBlur} disabled={locks.experienceYears} className="w-full px-5 py-3.5 bg-white/10 border border-white/20 rounded-2xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-400 outline-none transition-all text-white backdrop-blur-sm" />
                      {showErr('experienceYears') && <p className="text-red-400 text-xs mt-1 font-bold">{reqErrors.experienceYears}</p>}
                    </div>
                  </>
                )}

                {isOrganization && (
                  <>
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-white/80 ml-1">Organization Name {!form.organizationName && <span className="text-red-400 text-xs ml-2 uppercase font-black">Required</span>}</label>
                      <input name="organizationName" type="text" value={form.organizationName} onChange={onChange} onBlur={onBlur} disabled={locks.organizationName} className="w-full px-5 py-3.5 bg-white/10 border border-white/20 rounded-2xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-400 outline-none transition-all text-white backdrop-blur-sm" />
                      {showErr('organizationName') && <p className="text-red-400 text-xs mt-1 font-bold">{reqErrors.organizationName}</p>}
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-white/80 ml-1">Contact Name {!form.contactName && <span className="text-red-400 text-xs ml-2 uppercase font-black">Required</span>}</label>
                      <input name="contactName" type="text" value={form.contactName} onChange={onChange} onBlur={onBlur} disabled={locks.contactName} className="w-full px-5 py-3.5 bg-white/10 border border-white/20 rounded-2xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-400 outline-none transition-all text-white backdrop-blur-sm" />
                      {showErr('contactName') && <p className="text-red-400 text-xs mt-1 font-bold">{reqErrors.contactName}</p>}
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-white/80 ml-1">Location {!form.location && <span className="text-red-400 text-xs ml-2 uppercase font-black">Required</span>}</label>
                      <input name="location" type="text" value={form.location} onChange={onChange} onBlur={onBlur} disabled={locks.location} className="w-full px-5 py-3.5 bg-white/10 border border-white/20 rounded-2xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-400 outline-none transition-all text-white backdrop-blur-sm" />
                      {showErr('location') && <p className="text-red-400 text-xs mt-1 font-bold">{reqErrors.location}</p>}
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-white/80 ml-1">Website <span className="text-white/30 text-xs ml-2 uppercase tracking-tighter">Optional</span></label>
                      <input name="website" type="text" value={form.website} onChange={onChange} onBlur={onBlur} disabled={locks.website} className="w-full px-5 py-3.5 bg-white/10 border border-white/20 rounded-2xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-400 outline-none transition-all text-white backdrop-blur-sm" />
                    </div>
                  </>
                )}

                <div className="space-y-2 md:col-span-2">
                  <label className="text-sm font-bold text-white/80 ml-1">Phone Number {!form.phoneDisplay && <span className="text-red-400 text-xs ml-2 uppercase font-black">Required</span>}</label>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <input name="phone" type="text" value={form.phoneDisplay || ''} readOnly className="flex-1 px-5 py-3.5 bg-white/5 border border-white/10 rounded-2xl text-white/50 backdrop-blur-sm cursor-not-allowed" />
                    {!otpState.verified && (
                      <button type="button" className="px-10 py-3.5 bg-[#247E45] hover:bg-[#1e6a3a] text-white font-semibold text-lg rounded-full shadow-[0_8px_20px_rgba(36,126,69,0.3)] hover:shadow-[0_12px_24px_rgba(36,126,69,0.4)] hover:-translate-y-0.5 active:scale-[0.98] transition-all duration-300 disabled:opacity-50 disabled:hover:scale-100 disabled:hover:translate-y-0 disabled:shadow-none" onClick={requestOtp} disabled={otpState.requesting}>
                        {otpState.requesting ? 'Sending...' : otpState.requested ? 'Resend OTP' : 'Send OTP'}
                      </button>
                    )}
                  </div>

                  {!otpState.verified && otpState.requested && (
                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-4 p-4 bg-white/5 rounded-2xl border border-white/10">
                      <p className="text-xs font-bold text-white/60 mb-3 text-center uppercase tracking-widest">Enter Verification Code</p>
                      <div className="flex justify-center gap-3">
                        {Array.from({ length: OTP_LENGTH }).map((_, idx) => (
                          <input key={idx} ref={(el) => (otpInputRefs.current[idx] = el)} className={`w-12 h-14 sm:w-14 sm:h-16 text-center text-2xl font-black bg-white/10 border-2 rounded-xl text-white focus:ring-4 focus:ring-emerald-500/20 outline-none transition-all ${otpState.error ? 'border-red-500/50 focus:border-red-500' : otpState.verified ? 'border-emerald-500/50 focus:border-emerald-500' : 'border-white/20 focus:border-emerald-500'}`} type="text" inputMode="numeric" pattern="[0-9]*" maxLength={idx === 0 ? OTP_LENGTH : 1} value={(otpState.otp || '')[idx] || ''} onChange={(e) => handleOtpChange(idx, e.target.value)} onKeyDown={(e) => handleOtpKeyDown(e, idx)} />
                        ))}
                      </div>
                      {otpState.error && <p className="text-red-400 text-xs mt-3 text-center font-bold tracking-tight">{otpState.error}</p>}
                      <div className="flex justify-center mt-5">
                        <button type="button" onClick={handleVerifyOtp} disabled={otpState.verifying} className="px-12 py-3.5 bg-[#247E45] hover:bg-[#1e6a3a] text-white font-semibold text-lg rounded-full shadow-[0_8px_20px_rgba(36,126,69,0.3)] hover:shadow-[0_12px_24px_rgba(36,126,69,0.4)] hover:-translate-y-0.5 active:scale-[0.98] transition-all duration-300 disabled:opacity-50 disabled:hover:scale-100 disabled:hover:translate-y-0 disabled:shadow-none">
                          {otpState.verifying ? 'Verifying...' : 'Submit OTP'}
                        </button>
                      </div>
                    </motion.div>
                  )}
                  {otpState.verified && <div className="mt-2 flex items-center gap-2 text-emerald-400 text-sm font-bold"><span className="w-5 h-5 rounded-full bg-emerald-400/20 flex items-center justify-center">✓</span> Phone verified</div>}
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-bold text-white/80 ml-1">Email {locks.email && <span className="text-white/40 text-xs ml-2 uppercase">Locked</span>}</label>
                  <input name="email" type="text" value={form.email} onChange={onChange} onBlur={onBlur} disabled={locks.email} className={`w-full px-5 py-3.5 bg-white/5 border border-white/10 rounded-2xl text-white backdrop-blur-sm ${locks.email ? 'opacity-40 cursor-not-allowed' : ''}`} />
                  {showErr('email') && <p className="text-red-400 text-xs mt-1 font-bold">{reqErrors.email}</p>}
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-bold text-white/80 ml-1">Preferred Sport {locks.preferredSport && <span className="text-white/40 text-xs ml-2 uppercase">Locked</span>}</label>
                  <input name="preferredSport" type="text" value={form.preferredSport} onChange={onChange} onBlur={onBlur} disabled={locks.preferredSport} className={`w-full px-5 py-3.5 bg-white/5 border border-white/10 rounded-2xl text-white backdrop-blur-sm ${locks.preferredSport ? 'opacity-40 cursor-not-allowed' : ''}`} />
                  {showErr('preferredSport') && <p className="text-red-400 text-xs mt-1 font-bold">{reqErrors.preferredSport}</p>}
                </div>
              </div>

              <div className="mt-10 p-5 rounded-2xl bg-white/5 border border-white/5 text-white/60 text-sm leading-relaxed flex items-start gap-4">
                <span className="w-6 h-6 rounded-full bg-blue-600/30 border border-blue-500/50 text-blue-400 flex items-center justify-center shrink-0 mt-0.5 font-serif italic">i</span>
                Your data privacy is important to us. We use your information only to customize our product for you and never share it with third parties without your consent.
              </div>
            </div>

            {isPlayer && (
              <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="backdrop-blur-xl rounded-3xl p-6 md:p-10 border border-white/10 shadow-2xl" style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 100%)' }}>
                <div className="flex items-center justify-between mb-8 border-b border-white/10 pb-6">
                  <h2 className="text-2xl font-black text-white">Badminton Profile</h2>
                  <span className="text-white/40 text-xs font-black uppercase tracking-widest">Tailor your experience</span>
                </div>

                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-white/80 ml-1">Experience Level <span className="text-white/30 text-xs ml-2 uppercase tracking-tighter">Optional</span></label>
                      <select name="exp" value={form.exp} onChange={onChange} className="w-full px-5 py-3.5 bg-white/10 border border-white/20 rounded-2xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-400 outline-none transition-all text-white backdrop-blur-sm appearance-none">
                        <option value="" className="bg-gray-900">Select experience</option>
                        <option value="Beginner" className="bg-gray-900">Beginner</option>
                        <option value="Intermediate" className="bg-gray-900">Intermediate</option>
                        <option value="Experienced" className="bg-gray-900">Experienced</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-white/80 ml-1">Academy Name <span className="text-white/30 text-xs ml-2 uppercase tracking-tighter">Optional</span></label>
                      <input name="academyName" type="text" value={form.academyName} onChange={onChange} className="w-full px-5 py-3.5 bg-white/10 border border-white/20 rounded-2xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-400 outline-none transition-all text-white backdrop-blur-sm" />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-bold text-white/80 ml-1">Head Coach <span className="text-white/30 text-xs ml-2 uppercase tracking-tighter">Optional</span></label>
                    <input name="coachName" type="text" value={form.coachName} onChange={onChange} className="w-full px-5 py-3.5 bg-white/10 border border-white/20 rounded-2xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-400 outline-none transition-all text-white backdrop-blur-sm" />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-bold text-white/80 ml-1">Areas of Improvement <span className="text-white/30 text-xs ml-2 uppercase tracking-tighter">Optional</span></label>
                    <input name="areasText" type="text" placeholder="e.g. Footwork, Net play" value={form.areasText} onChange={onChange} className="w-full px-5 py-3.5 bg-white/10 border border-white/20 rounded-2xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-400 outline-none transition-all text-white backdrop-blur-sm" />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-white/80 ml-1">Goals <span className="text-white/30 text-xs ml-2 uppercase tracking-tighter">Optional</span></label>
                      <textarea name="goal" maxLength={256} placeholder="What are your goals with badminton?..." value={form.goal} onChange={onChange} className="w-full px-5 py-4 bg-white/10 border border-white/20 rounded-2xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-400 outline-none transition-all text-white backdrop-blur-sm min-h-[120px] resize-none" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-white/80 ml-1">Injuries <span className="text-white/30 text-xs ml-2 uppercase tracking-tighter">Optional</span></label>
                      <textarea name="injurySummary" placeholder="Any present or past injuries?..." value={form.injurySummary} onChange={onChange} className="w-full px-5 py-4 bg-white/10 border border-white/20 rounded-2xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-400 outline-none transition-all text-white backdrop-blur-sm min-h-[120px] resize-none" />
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        )}

        <div className="mt-16 sticky bottom-8 flex justify-center z-20 px-4">
          <button
            className="w-full max-w-sm py-4 bg-[#247E45] hover:bg-[#1e6a3a] text-white font-semibold text-xl rounded-full shadow-[0_8px_24px_rgba(36,126,69,0.3)] hover:shadow-[0_12px_30px_rgba(36,126,69,0.4)] hover:-translate-y-1 active:scale-[0.98] transition-all duration-300 disabled:opacity-50 disabled:hover:scale-100 disabled:hover:translate-y-0 disabled:shadow-none"
            onClick={handleSave}
            disabled={!requiredOK || !otpState.verified || saving}
          >
            {saving ? (
              <div className="flex items-center justify-center gap-3">
                <CircularProgress size={20} color="inherit" thickness={6} />
                <span>Saving...</span>
              </div>
            ) : (
              'Save & Continue'
            )}
          </button>
        </div>
          </>
        )}
      </div>
    </div>
  );
}
